/* Coria: sibling product choices preserve each product's original Shopify page. */
(() => {
  if (customElements.get('coria-product-choices')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const requests = new Map();
  const pageSize = 12;
  let sequence = 0;

  function getCatalog(url) {
    if (!requests.has(url)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      const pending = fetch(url, { signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Selector unavailable');
          return response.json();
        })
        .catch(error => { requests.delete(url); throw error; })
        .finally(() => clearTimeout(timeout));
      requests.set(url, pending);
    }
    return requests.get(url);
  }

  function thumbnail(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      // A small CDN rendition of the same original image; store images are untouched.
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', '96');
      return url.href;
    } catch (_) { return ''; }
  }

  function productImage(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', '1200');
      return url.href;
    } catch (_) { return ''; }
  }


  /* Coria: eticheta scurta pentru casutele de nuanta (casutele au ~57px pe telefon) */
  const cfShortLabel = (item) => {
    const raw = String(item.label || item.title || '').trim();
    if (raw.length <= 10) return raw;
    const stop = /^(baza|base|rubber|coria|oja|semipermanenta|gel|uv|led|top|coat|polygel|builder|polish|professional|perfect|ml|g|gr|de|pentru|unghii|color|colour|nr\.?)$/i;
    const words = raw.replace(/\b\d+\s*(ml|g|gr)\b/ig, ' ').split(/[\s,–-]+/).filter(Boolean);
    let numIdx = -1;
    words.forEach((w, i) => { if (/^(nr\.?)?\d{1,4}$/i.test(w)) numIdx = i; });
    if (numIdx >= 0) {
      const num = words[numIdx].replace(/^nr\.?/i, '');
      const before = words.slice(0, numIdx).filter(w => !stop.test(w));
      const name = before.length ? before[before.length - 1] : '';
      const lbl = (name ? name + ' ' : '') + num;
      return lbl.length <= 14 ? lbl : num;
    }
    const rest = words.filter(w => !stop.test(w));
    const t = rest.slice(-2).join(' ');
    return t.length > 14 ? (rest[rest.length - 1] || raw).slice(0, 14) : (t || raw.slice(0, 14));
  };
  // Coria: in loc de coria-variant-groups.json (2,9 MB, citit pe fiecare produs) se ia doar bucata produsului:
  // index coria-vg-iNN.json (NN = hash(handle) % 16) -> coria-vg-<id>.json. Generate de coria-tools/shard_manifest.py.
  // Orice lipsa (index vechi, bucata absenta, produs nou) -> fisierul complet, ca inainte.
  const loadManifest = async (url, handle) => {
    try {
      let x = 0x811c9dc5;
      for (let i = 0; i < handle.length; i++) { x ^= handle.charCodeAt(i); x = Math.imul(x, 0x01000193) >>> 0; }
      const part = name => url.replace('coria-variant-groups.json', name);
      const index = await getCatalog(part('coria-vg-i' + String(x % 16).padStart(2, '0') + '.json'));
      const id = index && index[handle];
      if (id) {
        const shard = await getCatalog(part('coria-vg-' + id + '.json'));
        if (shard && shard.byHandle && shard.families) return shard;
      }
    } catch (_) { /* revine la fisierul complet */ }
    return getCatalog(url);
  };
  class CoriaProductChoices extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        const data = await loadManifest(this.dataset.manifest, this.dataset.handle);
        this.catalog = data; // folosit de coria-choices-plus.js (stoc, pret, culoare)
        const manifestFamily = Object.keys(data.families || {}).find(name =>
          (data.families[name] || []).some(item => item.handle === this.dataset.handle)
        );
        const family = this.dataset.family || manifestFamily || '';
        if (family) {
          const families = { ...(data.families || {}) };
          const items = await this.familyItems(family, families);
          this.addFamily(family, items, families);
          this.hidden = !this.childElementCount;
          return;
        }
        const groups = (data.byHandle[this.dataset.handle] || [])
          .map(id => data.groups[id])
          .filter(group => group && group.items && group.items.length > 1)
          .sort((a, b) => (b.kind === 'volume') - (a.kind === 'volume'));
        for (const group of groups) this.addGroup(group);
        // Coria: orice alt produs (stickere, pile, accesorii...) primeste alternative din aceeasi categorie
        if (!this.childElementCount) { try { await this.addSimilarProducts(); } catch (_) { /* fara alternative */ } }
        this.hidden = !this.childElementCount;
      } catch (error) {
        this.replaceChildren();
        this.hidden = true;
        console.warn('Coria product choices:', error.message);
      }
    }

    async loadLiveFamily(family) {
      // Coria: produsele gamei se citesc live dupa eticheta "gama ..." (cautarea Shopify + sablonul search.cf-family),
      // asa ca orice produs nou cu eticheta potrivita apare singur in selector, cu volumul si nuanta/aroma lui.
      const familyTag = 'gama ' + normalize(family).trim();
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const results = [];
      try {
        for (let page = 1, pages = 1; page <= pages && page <= 8; page++) {
          const url = root + 'search?q=' + encodeURIComponent('tag:"' + familyTag + '"') + '&type=product&options%5Bprefix%5D=none&view=cf-family&page=' + page;
          const data = await getCatalog(url);
          pages = Number(data.pages) || 1;
          results.push(...(data.products || []).filter(item => (item.tags || []).some(tag => normalize(tag).trim() === familyTag)));
        }
      } catch (_) { /* rezerva mai jos: colectiile produsului */ }
      if (!results.length) {
        const expected = normalize(family).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const familyWords = expected.split('-').filter(Boolean);
        const candidates = String(this.dataset.collections || '').split(',').filter(Boolean).filter(handle => {
          const words = normalize(handle).split(/[^a-z0-9]+/).filter(Boolean);
          return familyWords.every(word => words.includes(word));
        });
        const lists = await Promise.all(candidates.map(handle => getCatalog(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250').catch(() => ({}))));
        for (const item of lists.flatMap(data => data.products || [])) {
          if (!Array.isArray(item.tags) || !item.tags.some(tag => normalize(tag).trim() === familyTag)) continue;
          const variants = item.variants || [];
          const pick = variants.find(v => v.available) || variants[0] || {};
          results.push({ handle: item.handle, title: item.title || '', image: item.images?.[0]?.src || '', type: item.product_type || '',
            a: variants.some(v => v.available) ? 1 : 0, p: pick.price || '', d: String(item.published_at || item.created_at || '').slice(0, 10), v: pick.id });
        }
      }
      const volRe = /\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/i;
      const live = results.map(item => {
        const title = item.title || '';
        const volume = (title.match(volRe) || [''])[0].replace(/\s+/g, '');
        const bare = title.replace(new RegExp(volRe.source, 'ig'), '').replace(/\s+/g, ' ').trim();
        let label;
        if (/oja|gel|rubber|polygel|baza|pigment/i.test(item.type + ' ' + family)) {
          const m = bare.match(/(?:nr\.?\s*)?(\d{1,4}[a-z]?)\s*$/i);
          label = m ? m[1] : bare;
        } else if (/cleaner/i.test(family)) {
          /* numele se afiseaza cu literele originale (Lost Cherryx, nu lost cherryx); comparatiile folosesc normalize() separat */
          label = bare.replace(/\b(cleaner|coria|cozy|seductive|copy)\b/ig, '').replace(/\s+/g, ' ').trim();
        } else {
          label = bare.charAt(0).toUpperCase() + bare.slice(1);
        }
        const aromaTag = (item.tags || []).find(tag => /^aroma\s/i.test(String(tag).trim()));
        return { handle: item.handle, title, image: item.image || '', volume, label, family, type: normalize(item.type || ''),
          aroma: aromaTag ? String(aromaTag).trim().replace(/^aroma\s+/i, '') : '',
          a: item.a, p: item.p, d: item.d, v: item.v, r: item.r, rc: item.rc };
      });
      (this.liveItems || (this.liveItems = [])).push(...live);
      return live;
    }

    // static + live, fara dubluri (datele statice raman, cele live completeaza stocul, pretul si produsele noi)
    mergeFamily(staticItems, liveItems) {
      const byHandle = new Map();
      for (const item of staticItems || []) if (item?.handle) byHandle.set(item.handle, { ...item });
      for (const item of liveItems || []) {
        if (!item?.handle) continue;
        const known = byHandle.get(item.handle);
        if (known) Object.assign(known, { a: item.a, p: item.p, v: item.v, d: known.d || item.d, aroma: item.aroma || known.aroma || '' });
        else byHandle.set(item.handle, item);
      }
      return [...byHandle.values()];
    }

    async familyItems(name, allFamilies) {
      this.familyCache = this.familyCache || {};
      if (!this.familyCache[name]) {
        let live = [];
        try { live = await this.loadLiveFamily(name); } catch (_) {}
        this.familyCache[name] = this.mergeFamily(allFamilies[name] || [], live);
        allFamilies[name] = this.familyCache[name];
      }
      return this.familyCache[name];
    }

    // Coria: lista live de produse dupa o interogare de cautare (tip de produs) sau dupa o colectie
    async loadQuery(query, maxPages) {
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const out = [];
      for (let page = 1, pages = 1; page <= pages && page <= (maxPages || 2); page++) {
        const data = await getCatalog(root + 'search?q=' + encodeURIComponent(query) + '&type=product&options%5Bprefix%5D=none&view=cf-family&page=' + page);
        pages = Number(data.pages) || 1;
        out.push(...(data.products || []));
      }
      return out;
    }

    async loadCollection(handle) {
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const data = await getCatalog(root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250');
      return (data.products || []).map(item => {
        const variants = item.variants || [];
        const pick = variants.find(v => v.available) || variants[0] || {};
        return { handle: item.handle, title: item.title || '', image: item.images?.[0]?.src || '', type: item.product_type || '',
          a: variants.some(v => v.available) ? 1 : 0, p: pick.price || '', d: String(item.published_at || '').slice(0, 10), v: pick.id };
      });
    }

    async addSimilarProducts() {
      if (this.dataset.similar === 'off') return;
      const type = (this.dataset.type || '').trim();
      const collection = (this.dataset.collection || '').trim();
      let list = [];
      let from = '';
      if (type) {
        list = (await this.loadQuery('product_type:"' + type.replace(/"/g, '') + '"', 2)).filter(item => normalize(item.type) === normalize(type));
        from = type;
      }
      if (list.length < 2 && collection) {
        list = await this.loadCollection(collection);
        from = this.dataset.collectionTitle || collection;
      }
      const unique = new Map();
      for (const item of list) if (item?.handle) unique.set(item.handle, item);
      if (!unique.has(this.dataset.handle)) {
        unique.set(this.dataset.handle, { handle: this.dataset.handle, title: this.dataset.currentTitle || '', image: this.dataset.currentImage || '', type });
      }
      const items = [...unique.values()];
      if (items.length < 2) return;
      // eticheta scurta: fara cuvintele comune de la inceput (ex. "Sticker Unghii") si fara volum
      const volRe = /\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/i;
      const words = items.map(item => String(item.title || '').replace(new RegExp(volRe.source, 'ig'), '').replace(/\s+/g, ' ').trim().split(' '));
      let common = 0;
      while (common < 4 && words.every(w => w.length > common + 1 && normalize(w[common]) === normalize(words[0][common]))) common++;
      const freq = new Map();
      for (const w of words) for (const word of new Set(w.map(normalize))) freq.set(word, (freq.get(word) || 0) + 1);
      const frequent = new Set([...freq].filter(([, n]) => words.length >= 4 && n / words.length >= .7).map(([word]) => word));
      const shaped = items.map((item, index) => ({
        ...item,
        family: from,
        volume: (String(item.title || '').match(volRe) || [''])[0].replace(/\s+/g, ''),
        label: (words[index].slice(common).filter(word => !frequent.has(normalize(word))).join(' ') || words[index].slice(common).join(' ') || item.title).replace(/^[\s\-\/|·,]+/, '').replace(/^(de|pentru|cu|si|la|din)\s+/i, ''),
        type: normalize(item.type || type)
      }));
      (this.liveItems || (this.liveItems = [])).push(...shaped);
      this.chooseTitle = 'Alege din ' + (from ? from.toLowerCase() : 'aceeasi categorie');
      this.classList.add('cfc-similar');
      this.addFamily('cf-similar ' + from, shaped, {});
    }

    addFamily(family, items, allFamilies) {
      if (/cleaner/i.test(family)) return this.addCleanerFamily(family, items);
      if (/^(rubber base|builder gel|polygel|oja semipermanenta)$/i.test(items.find(item => item.type)?.type || '')) {
        return this.addCollectionFamily(family, items, allFamilies);
      }
      const handle = this.dataset.selectedHandle || this.dataset.handle;
      const unique = new Map();
      for (const item of items) if (item?.handle) unique.set(item.handle, item);
      const cards = new Map();
      for (const item of unique.values()) {
        const key = normalize(item.label || item.title).replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/ig, '').trim();
        if (!cards.has(key)) cards.set(key, { label: item.label || item.title, image: item.image, options: [] });
        const card = cards.get(key);
        if (!card.image && item.image) card.image = item.image;
        card.options.push(item);
      }
      const current = [...cards.values()].find(card => card.options.some(item => item.handle === handle));
      if (!current || (cards.size < 2 && current.options.length < 2)) return;
      const block = document.createElement('section');
      block.className = 'coria-choice-group coria-family-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      const kind = /cleaner|cuticule|ulei|spuma|hydrogel/i.test(family) ? 'aroma' : /abtibild|sablon|sticker|folie|folii|decor|matrit|stampil|banda/i.test(family + ' ' + (items.find(item => item.type)?.type || '')) ? 'modelul' : 'nuanta';
      title.textContent = this.chooseTitle || ('Alege ' + kind);
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      const currentItem = current.options.find(item => item.handle === handle) || current.options[0] || {};
      selected.textContent = (currentItem.title || current.label || '') + (currentItem.volume ? ' · ' + currentItem.volume : '');
      heading.append(title, selected);
      block.append(heading);

      const list = document.createElement('div');
      list.className = 'coria-family-list';
      const allCards = [...cards.values()];
      for (const card of allCards) card.options.sort((a, b) => (parseFloat(a.volume) || 0) - (parseFloat(b.volume) || 0));
      /* 18.10.2026: doua linii cu acelasi numar (ex. Marble Ink 03 + Marble Ink Metallic 03) stau pe acelasi card;
         poza si primul rand sunt ale liniei pe care e clienta, nu ale primei gasite */
      const stemOf = (h) => String(h || '').replace(/[-_]?[a-z]*\d+[a-z]*$/i, '');
      const lineStem = stemOf(handle);
      for (const card of allCards) {
        const same = card.options.filter(item => stemOf(item.handle) === lineStem);
        if (!same.length) continue;
        card.options = same.concat(card.options.filter(item => !same.includes(item)));
        if (same[0].image) card.image = same[0].image;
      }
      let query = '';
      let limit = pageSize;
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'coria-choice-more';
      more.textContent = 'Arată mai multe';
      more.setAttribute('aria-label', 'Arată mai multe opțiuni: ' + title.textContent.toLowerCase());
      const draw = () => {
        const matched = allCards.filter(card => !query || normalize(card.label).includes(query));
        matched.sort((a, b) => (b.options.some(item => item.handle === handle)) - (a.options.some(item => item.handle === handle)));
        list.replaceChildren();
        for (const card of matched.slice(0, limit)) {
          const tile = document.createElement('div');
          tile.className = 'coria-family-card';
          if (card.options.some(item => item.handle === handle)) tile.classList.add('is-current');
          if (card.image) {
            const img = document.createElement('img');
            img.loading = 'lazy'; img.decoding = 'async'; img.width = 42; img.height = 42; img.alt = '';
            img.src = thumbnail(card.image);
            img.addEventListener('error', () => img.remove(), { once: true });
            tile.append(img);
          }
          const name = document.createElement('span');
          name.className = 'coria-family-name'; { const t0 = String((card.options[0] && card.options[0].title) || '').replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|l|g|gr|kg)\b/ig, '').replace(/\s+/g, ' ').trim(); const shown = (t0 && normalize(t0).replace(/[^a-z0-9]+/g, ' ').trim() === (normalize(card.label).replace(/[^a-z0-9]+/g, ' ').trim())) ? t0 : String(card.label || ''); name.textContent = shown.charAt(0).toUpperCase() + shown.slice(1); }
          tile.append(name);
          const options = document.createElement('div');
          options.className = 'coria-family-sizes';
          // doua variante cu acelasi volum in acelasi card (ex. Marble Ink si Marble Ink Metallic, ambele „15 ml”):
          // eticheta primeste cuvantul care le deosebeste din titlu („Metallic 15 ml”), si pe ecran si pentru cititorul de ecran
          const vols = card.options.map(o => o.volume || '');
          const extra = item => {
            const others = card.options.filter(o => o !== item).map(o => String(o.title || '').toLowerCase());
            return String(item.title || '').split(/\s+/).filter(w => w.length > 2 && !/\d/.test(w) && others.every(t => !t.includes(w.toLowerCase()))).join(' ');
          };
          for (const item of card.options) {
            const link = document.createElement('button');
            link.className = 'coria-family-size';
            link.type = 'button';
            link.dataset.handle = item.handle;
            let label = item.volume || '';
            if (label && vols.filter(v => v === label).length > 1) { const x = extra(item); if (x) label = x + ' ' + label; }
            link.textContent = label || (item.handle === handle ? 'Ales' : 'Alege');
            link.title = item.title;
            link.setAttribute('aria-label', `${card.label}, ${label || 'produs'}`);
            link.setAttribute('aria-pressed', item.handle === handle ? 'true' : 'false');
            if (item.handle === handle) link.classList.add('is-current');
            link.addEventListener('click', () => this.selectProduct(item, link, selected, status, block));
            options.append(link);
          }
          tile.append(options); list.append(tile);
        }
        status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} opțiuni` : 'Nu există rezultate.';
        more.hidden = limit >= matched.length;
      };
      if (allCards.length > pageSize) {
        const input = document.createElement('input');
        input.type = 'search';
        input.placeholder = this.chooseTitle ? 'Cauta in ' + this.chooseTitle.replace(/^Alege din /, '') : (kind === 'aroma' ? 'Caută aroma' : kind === 'modelul' ? 'Caută modelul' : 'Caută nuanța');
        input.setAttribute('aria-label', 'Caută: ' + title.textContent.toLowerCase());
        input.className = 'coria-choice-search';
        input.autocomplete = 'off';
        input.addEventListener('input', () => { query = normalize(input.value); limit = pageSize; draw(); });
        block.append(input);
      }
      more.addEventListener('click', () => { limit += pageSize; draw(); });
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status, more);
      block.append(list, footer);
      this.append(block);
      draw();
    }

    makeBlock(titleText, currentText) {
      const block = document.createElement('section');
      block.className = 'coria-choice-group coria-family-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      title.textContent = titleText;
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      selected.textContent = currentText || '';
      heading.append(title, selected);
      block.append(heading);
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status);
      block.append(footer);
      this.append(block);
      return { block, title, selected, status };
    }

    addCleanerFamily(family, items) {
      const unique = new Map();
      for (const item of items) if (item?.handle) unique.set(item.handle, item);
      const products = [...unique.values()];
      const current = products.find(item => item.handle === (this.dataset.selectedHandle || this.dataset.handle));
      if (!current) return;
      const aromas = new Map();
      const volumes = new Set();
      for (const item of products) {
        const label = item.label || item.title.replace(/\bcleaner\b/ig, '').replace(/\b\d+(?:[.,]\d+)?\s*(?:ml|l)\b/ig, '').trim();
        // aceeasi aroma la toate marimile: intai eticheta „aroma ...”, apoi titlul fara cuvintele de gama (Coria, Cozy, Seductive)
        const key = normalize(item.aroma || label).replace(/\b(cleaner|coria|cozy|seductive|copy)\b/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
        const volume = normalize(item.volume);
        if (!key || !volume) continue;
        volumes.add(volume);
        if (!aromas.has(key)) aromas.set(key, { label, image: item.image || '', options: [] });
        const aroma = aromas.get(key);
        if (!aroma.image && item.image) aroma.image = item.image;
        aroma.options.push({ ...item, label, _volume: volume });
      }
      const sortedVolumes = [...volumes].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));
      const groups = this.makeBlock('Alege cantitatea', current.title);
      groups.block.classList.add('coria-cleaner-selector');
      const volumeList = document.createElement('div');
      volumeList.className = 'coria-choice-list is-volume coria-cleaner-volumes';
      const aromaHeading = document.createElement('div');
      aromaHeading.className = 'coria-choice-heading coria-aroma-heading';
      const aromaTitle = document.createElement('h3');
      aromaTitle.textContent = 'Alege aroma';
      const selectedVolume = normalize(current.volume);
      const aromaList = document.createElement('div');
      aromaList.className = 'coria-family-list coria-cleaner-aromas';
      let volume = sortedVolumes.includes(selectedVolume) ? selectedVolume : sortedVolumes[0];
      let query = '';
      let limit = pageSize;
      const search = document.createElement('input');
      search.type = 'search';
      search.placeholder = 'Caută aroma';
      search.setAttribute('aria-label', 'Caută aroma');
      search.className = 'coria-choice-search';
      search.autocomplete = 'off';
      search.addEventListener('input', () => { query = normalize(search.value); limit = pageSize; drawAromas(); });
      const more = document.createElement('button');
      more.type = 'button'; more.className = 'coria-choice-more'; more.textContent = 'Arată mai multe';
      const drawAromas = () => {
        const matched = [...aromas.values()].map(aroma => ({ ...aroma, item: aroma.options.find(option => option._volume === volume) }))
          .filter(aroma => aroma.item && (!query || normalize(aroma.label).includes(query)));
        matched.sort((a, b) => (b.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) - (a.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) || a.label.localeCompare(b.label));
        aromaList.replaceChildren();
        for (const aroma of matched.slice(0, limit)) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'coria-family-size coria-aroma-option';
          button.dataset.handle = aroma.item.handle;
          button.setAttribute('aria-pressed', aroma.item.handle === (this.dataset.selectedHandle || this.dataset.handle) ? 'true' : 'false');
          if (aroma.item.handle === (this.dataset.selectedHandle || this.dataset.handle)) button.classList.add('is-current');
          if (aroma.image) {
            const image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async'; image.width = 34; image.height = 34; image.alt = ''; image.src = thumbnail(aroma.image); button.append(image);
          }
          const name = document.createElement('span'); name.textContent = String(aroma.label || '').replace(/\S+/g, w => /^(i{1,3}|iv|vi{0,3}|x)$/i.test(w) ? w.toUpperCase() : (w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1) : w)); button.append(name);
          button.addEventListener('click', async () => {
            await this.selectProduct(aroma.item, button, groups.selected, groups.status, groups.block);
            drawVolumes(); /* aroma noua -> marimile ei */
          });
          aromaList.append(button);
        }
        groups.status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} arome disponibile la ${volume}` : `Nu există arome disponibile la ${volume}.`;
        more.hidden = limit >= matched.length;
      };
      // la marimi apar DOAR cele pe care le are aroma aleasa (Lost Cherry: 100/400/1000, Scandal: 100/250/1000)
      const currentAromaOf = () => [...aromas.values()].find(aroma => aroma.options.some(option => option.handle === (this.dataset.selectedHandle || this.dataset.handle)));
      const drawVolumes = () => {
        volumeList.replaceChildren();
        const ownAroma = currentAromaOf();
        const shown = ownAroma ? sortedVolumes.filter(value => ownAroma.options.some(option => option._volume === value)) : sortedVolumes;
        for (const value of (shown.length ? shown : sortedVolumes)) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'coria-choice'; button.textContent = value;
          button.setAttribute('aria-pressed', value === volume ? 'true' : 'false');
          if (value === volume) button.classList.add('is-current');
          button.addEventListener('click', () => {
            if (value === volume) return;
            // aroma ramane aceeasi la marimea noua (daca exista); o calculam INAINTE de redesenare, ca sa apara prima in lista
            const currentAroma = [...aromas.values()].find(aroma => aroma.options.some(option => option.handle === (this.dataset.selectedHandle || this.dataset.handle)));
            volume = value; limit = pageSize;
            const replacement = currentAroma?.options.find(option => option._volume === volume) || [...aromas.values()].map(aroma => aroma.options.find(option => option._volume === volume)).find(Boolean);
            if (replacement) this.dataset.selectedHandle = replacement.handle;
            drawVolumes(); drawAromas();
            if (replacement) {
              const targetButton = [...aromaList.querySelectorAll('[data-handle]')].find(el => el.dataset.handle === replacement.handle);
              if (targetButton) this.selectProduct(replacement, targetButton, groups.selected, groups.status, groups.block);
            }
          });
          volumeList.append(button);
        }
      };
      more.addEventListener('click', () => { limit += pageSize; drawAromas(); });
      aromaHeading.append(aromaTitle);
      groups.block.append(volumeList, aromaHeading, search, aromaList, more);
      drawVolumes(); drawAromas();
    }

    addCollectionFamily(family, items, allFamilies) {
      const type = normalize(items.find(item => item.type)?.type || '');
      const collections = Object.entries(allFamilies).filter(([, products]) =>
        (products || []).some(item => normalize(item.type) === type)
      );
      if (collections.length < 2) return this.addFamilyFallback(family, items);
      const current = items.find(item => item.handle === (this.dataset.selectedHandle || this.dataset.handle));
      if (!current) return;
      const groups = this.makeBlock('Alege colecția', current.title);
      groups.block.classList.add('coria-gel-selector');
      const collectionList = document.createElement('div');
      collectionList.className = 'coria-choice-list coria-collection-list';
      const shadeHeading = document.createElement('div'); shadeHeading.className = 'coria-choice-heading coria-shade-heading';
      const shadeTitle = document.createElement('h3'); shadeTitle.textContent = 'Alege culoarea'; shadeHeading.append(shadeTitle);
      const shades = document.createElement('div'); shades.className = 'coria-choice-list coria-shade-list';
      const drawCollections = () => {
        collectionList.replaceChildren();
        for (const [name] of collections) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'coria-choice coria-collection-option';
          button.textContent = name.replace(/\b\w/g, char => char.toUpperCase());
          button.setAttribute('aria-pressed', name === family ? 'true' : 'false');
          if (name === family) button.classList.add('is-current');
          button.addEventListener('click', async () => {
            family = name;
            button.setAttribute('aria-busy', 'true');
            items = [...await this.familyItems(name, allFamilies)];
            button.removeAttribute('aria-busy');
            drawCollections(); drawShades();
            const target = items.find(item => item.label === current.label) || items[0];
            const targetButton = [...shades.querySelectorAll('[data-handle]')].find(el => el.dataset.handle === target?.handle);
            if (target && targetButton) this.selectProduct(target, targetButton, groups.selected, groups.status, groups.block);
          });
          collectionList.append(button);
        }
      };
      const drawShades = () => {
        shades.replaceChildren();
        const seen = new Set();
        const available = items.filter(item => {
          const key = normalize(item.label || item.title);
          if (!item.handle || seen.has(key)) return false;
          seen.add(key); return true;
        });
        /* eticheta: doar numarul nuantei; numele familiei apare doar cand doua nuante au acelasi numar */
        const cfLbl = new Map(), cfNum = new Map();
        available.forEach(it => { const l = cfShortLabel(it); const m = l.match(/(\d{1,4})$/); const n = m ? String(+m[1]) : null; cfLbl.set(it.handle, [l, m ? m[1] : null]); if (n) cfNum.set(n, (cfNum.get(n) || 0) + 1); });
        const cfPick = it => { const v = cfLbl.get(it.handle); if (!v || !v[1]) return v ? v[0] : cfShortLabel(it); return cfNum.get(String(+v[1])) > 1 ? v[0] : v[1]; };
        for (const item of available) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'coria-choice coria-shade-option';
          button.dataset.handle = item.handle; button.title = item.title; button.setAttribute('aria-label', item.title);
          button.setAttribute('aria-pressed', item.handle === (this.dataset.selectedHandle || this.dataset.handle) ? 'true' : 'false');
          if (item.handle === (this.dataset.selectedHandle || this.dataset.handle)) button.classList.add('is-current');
          if (item.image) { const image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async'; image.width = 42; image.height = 42; image.alt = ''; image.src = thumbnail(item.image); button.append(image); }
          const label = document.createElement('span'); label.textContent = cfPick(item); if (label.textContent.length >= 4) label.classList.add('cfc-long'); button.append(label);
          button.addEventListener('click', () => this.selectProduct(item, button, groups.selected, groups.status, groups.block));
          shades.append(button);
        }
        /* 19.10.2026: colectie cu o singura nuanta (ex. Builder Milky White LEVEL 1): „Alege culoarea” cu o bulina „1” nu alege nimic, o ascundem */
        const cfSolo = available.length < 2;
        shadeHeading.hidden = cfSolo; shades.hidden = cfSolo;
        shadeHeading.style.display = shades.style.display = cfSolo ? 'none' : '';
      };
      groups.block.append(collectionList, shadeHeading, shades);
      drawCollections(); drawShades();
    }

    addFamilyFallback(family, items) {
      // A single collection still gets the standard shade chooser.
      const handle = this.dataset.selectedHandle || this.dataset.handle;
      const current = items.find(item => item.handle === handle);
      if (!current || items.length < 2) return;
      const groups = this.makeBlock('Alege culoarea', current.title);
      const list = document.createElement('div'); list.className = 'coria-choice-list coria-shade-list cf-shade-cards';
      for (const item of items) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'coria-choice coria-shade-option'; button.dataset.handle = item.handle; button.title = item.title; if (item.image) { const image = document.createElement('img'); image.loading = 'lazy'; image.decoding = 'async'; image.width = 64; image.height = 64; image.alt = ''; image.src = thumbnail(item.image); image.addEventListener('error', () => image.remove(), { once: true }); button.append(image); } const label = document.createElement('span'); label.textContent = cfShortLabel(item); button.append(label);
        button.setAttribute('aria-pressed', item.handle === handle ? 'true' : 'false');
        if (item.handle === handle) button.classList.add('is-current');
        button.addEventListener('click', () => this.selectProduct(item, button, groups.selected, groups.status, groups.block));
        list.append(button);
      }
      groups.block.append(list);
    }

    addGroup(group) {
      const handle = this.dataset.handle;
      const current = group.items.find(item => item.handle === handle);
      if (!current) return;
      const block = document.createElement('section');
      block.className = 'coria-choice-group';
      const heading = document.createElement('div');
      heading.className = 'coria-choice-heading';
      const title = document.createElement('h3');
      const shade = /^(rubber|builder|oja|polygel)\b/.test(group.name);
      title.textContent = group.kind === 'volume' ? 'Cantitate' : group.kind === 'aroma' ? 'Aromă' : shade ? 'Nuanță' : 'Model';
      title.id = `coria-choice-heading-${++sequence}`;
      block.setAttribute('aria-labelledby', title.id);
      const selected = document.createElement('p');
      selected.className = 'coria-current-choice';
      selected.textContent = current.label;
      heading.append(title, selected);
      block.append(heading);

      const list = document.createElement('div');
      list.className = 'coria-choice-list' + (group.kind === 'volume' ? ' is-volume' : group.kind === 'aroma' ? ' is-aroma' : '');
      let query = '';
      let limit = pageSize;
      const status = document.createElement('p');
      status.className = 'coria-choice-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'coria-choice-more';
      more.textContent = 'Arată mai multe';
      more.setAttribute('aria-label', 'Arată mai multe opțiuni: ' + title.textContent.toLowerCase());

      const draw = () => {
        const matched = group.items.filter(item => !query || normalize(item.label + ' ' + item.title).includes(query));
        matched.sort((a, b) => (b.handle === handle) - (a.handle === handle));
        list.replaceChildren();
        for (const item of matched.slice(0, limit)) {
          const link = document.createElement('button');
          link.className = 'coria-choice';
          link.type = 'button';
          link.dataset.handle = item.handle;
          link.title = item.title;
          link.setAttribute('aria-label', item.title);
          if (item.handle === handle) {
            link.classList.add('is-current');
            link.setAttribute('aria-pressed', 'true');
          } else {
            link.setAttribute('aria-pressed', 'false');
          }
          if (group.kind !== 'volume') {
            const src = thumbnail(item.handle === handle && this.dataset.currentImage ? this.dataset.currentImage : item.image);
            if (src) {
              const img = document.createElement('img');
              img.loading = 'lazy';
              img.decoding = 'async';
              img.width = 42;
              img.height = 42;
              img.alt = '';
              img.src = src;
              img.addEventListener('error', () => img.remove(), { once: true });
              link.append(img);
            }
          }
          const label = document.createElement('span');
          label.textContent = item.label;
          link.append(label);
          link.addEventListener('click', () => this.selectProduct(item, link, selected, status, block));
          list.append(link);
        }
        status.textContent = matched.length ? `${Math.min(limit, matched.length)} din ${matched.length} opțiuni` : 'Nu există rezultate.';
        more.hidden = limit >= matched.length;
      };

      if (group.items.length > pageSize) {
        const input = document.createElement('input');
        input.type = 'search';
        input.placeholder = group.kind === 'aroma' ? 'Caută aroma' : 'Caută numărul sau numele';
        input.setAttribute('aria-label', 'Caută: ' + title.textContent.toLowerCase());
        input.className = 'coria-choice-search';
        input.autocomplete = 'off';
        input.addEventListener('input', () => { query = normalize(input.value); limit = pageSize; draw(); });
        block.append(input);
      }
      more.addEventListener('click', () => { limit += pageSize; draw(); });
      const footer = document.createElement('div');
      footer.className = 'coria-choice-footer';
      footer.append(status, more);
      block.append(list, footer);
      this.append(block);
      draw();
    }

    async selectProduct(item, button, selected, status, block) {
      if (!item?.handle || button.disabled) return;
      const root = (this.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const section = this.closest('.section-main-product') || document.querySelector('.section-main-product');
      const form = section?.querySelector('product-form-context form[data-type="add-to-cart-form"]');
      const variantId = form?.querySelector('[name="id"]');
      if (!form || !variantId) {
        status.textContent = 'Nu am putut pregăti produsul pentru coș. Reîncarcă pagina și încearcă din nou.';
        return;
      }
      /* 27.10: doua alegeri rapide se puteau suprapune (raspunsul vechi venea dupa cel nou) -> in cos intra alt produs decat cel afisat.
         Fiecare alegere are un numar; raspunsurile depasite se ignora, iar „Adauga in cos” e oprit cat se actualizeaza produsul. */
      const seq = this.__cfSelSeq = (this.__cfSelSeq || 0) + 1;
      const stale = () => seq !== this.__cfSelSeq;
      const buyControls = section.querySelectorAll('[data-add-to-cart], [data-sticky-buy-button]');
      buyControls.forEach(control => { control.disabled = true; control.setAttribute('aria-disabled', 'true'); });
      /* 27.10 (audit NOU 6): o cerere a selectorului nativ de variante (variants-set) pornita inainte putea rescrie mai tarziu
         varianta din formular cu produsul vechi -> o oprim (are propriul AbortController) */
      /* 27.10 (runda 4): inainte de orice await — generatie noua (raspunsurile native pornite inainte sunt ignorate in theme.js)
         si selectorul nativ blocat cat dureaza alegerea Coria (deblocat la final doar daca e al produsului ales) */
      const cfHostV300 = section.closest('[id^="shopify-section-"]') || section; /* runda 8: aceeasi instanta ca in theme.js */
      cfHostV300.__cfSelGen = (cfHostV300.__cfSelGen || 0) + 1;
      /* runda 9: numarul cererii native e pe formular (theme.js) -> cererile native ale formularelor din sectiune sunt incheiate (oprite mai jos); formularul si butoanele sunt acum ale alegerii Coria */
      cfHostV300.querySelectorAll('form[action*="/cart/add"]').forEach(f => { f.__cfNatApplied = f.__cfNatReq || 0; });
      section.querySelectorAll('variants-set').forEach(v => { try { if (v.abortController) v.abortController.abort(); v.pendingRequestUrl = null; v.inert = true; v.dataset.cfLocked = '1'; } catch (_) {} });
      button.disabled = true;
      block.setAttribute('aria-busy', 'true');
      status.textContent = 'Se actualizează produsul…';
      let buyDone = false;
      let aplicat = false; /* perf 18.10: daca nu putem schimba produsul in pagina (429 / retea), deschidem pagina nuantei */
      try {
        const product = await getCatalog(root + 'products/' + encodeURIComponent(item.handle) + '.js');
        if (stale()) return;
        const variant = (product.variants || []).find(value => value.available) || (product.variants || [])[0];
        if (!variant) throw new Error('Nu există variante pentru produs.');
        variantId.value = variant.id;
        aplicat = true;
        variantId.disabled = false;
        variantId.dispatchEvent(new Event('change', { bubbles: true }));
        /* 19.10.2026 (CHAT-NOU): si formularul de rate din aceeasi sectiune primeste varianta noua */
        const host = section.closest('[id^="shopify-section-"]') || document;
        host.querySelectorAll('form.installment [name="id"]').forEach((input) => { input.value = variant.id; });
        /* 27.10 (audit): cantitatea poate fi in afara formularului (atributul form=) -> o cautam si acolo; valoarea intra in min/max/pas */
        const quantity = form.querySelector('[name="quantity"]')
          || (form.id && document.querySelector('[name="quantity"][form="' + CSS.escape(form.id) + '"]'))
          || section.querySelector('[name="quantity"]');
        if (quantity) {
          /* 27.10 (audit NOU 6): regulile produsului ales (sau cele implicite, daca lipsesc), in atribute SI in data-* (le citeste
             componenta temei), iar maximul scade cu cantitatea deja in cos, ca la componenta nativa (theme.js setQuantityBoundries) */
          const rule = variant.quantity_rule || {};
          const qMin = Number(rule.min) || 1, qStep = Number(rule.increment) || 1;
          const qMax = rule.max != null && rule.max !== '' ? Number(rule.max) : null;
          let inCart = 0, cosCunoscut = false;
          /* 27.10 (audit NOU 6): /cart.js cu limita de 3 s; daca nu raspunde, mergem mai departe fara scaderea din cos */
          try { const acC = typeof AbortController === 'function' ? new AbortController() : null; const toC = acC ? setTimeout(() => acC.abort(), 3000) : 0; const cart = await fetch(root + 'cart.js', { credentials: 'same-origin', signal: acC ? acC.signal : undefined }).then(r => r.json()).finally(() => clearTimeout(toC)); (cart.items || []).forEach(it => { if (String(it.variant_id) === String(variant.id)) inCart += Number(it.quantity) || 0; }); cosCunoscut = true; } catch (_) {}
          if (stale()) return;
          /* 27.10 (audit NOU 6): daca cosul nu a raspuns, NU il consideram gol: pastram cantitatea din cos cunoscuta deja pentru aceasta varianta
             (data-cart-quantity, daca e a ei) sau 0, dar fara sa ridicam maximul peste cel brut */
          if (!cosCunoscut && quantity.dataset.cartVariant === String(variant.id)) inCart = Number(quantity.dataset.cartQuantity) || 0;
          quantity.dataset.min = qMin; quantity.dataset.cartQuantity = inCart; quantity.dataset.cartVariant = String(variant.id); quantity.dataset.cartUnknown = cosCunoscut ? '' : '1';
          if (qMax != null) quantity.dataset.max = qMax; else delete quantity.dataset.max;
          const left = qMax == null ? null : Math.max(0, qMax - inCart);
          let lo = qMin; if (left !== null) lo = Math.min(lo, left); if (inCart >= qMin) lo = Math.min(lo, qStep);
          quantity.min = Math.max(lo, left === 0 ? 0 : 1);
          quantity.step = qStep;
          if (left !== null) quantity.max = left; else quantity.removeAttribute('max');
          let q = Math.max(Number(quantity.value) || Number(quantity.min), Number(quantity.min));
          q = Number(quantity.min) + Math.round((q - Number(quantity.min)) / qStep) * qStep;
          if (left !== null && q > left) q = left - ((left - Number(quantity.min)) % qStep);
          quantity.value = Math.max(q, Number(quantity.min));
        }
        const canAdd = Boolean(variant.available);
        /* 27.10 (audit): „Adauga in cos” se reactiveaza abia la final, dupa galerie si marcajul selectiei (vezi mai jos) */
        section.querySelectorAll('.product-title-heading, .sticky--product-card .product--title').forEach(node => { node.textContent = product.title; });
        // formatul temei contine HTML (<span class=money>...</span> Lei): il curatam, ca pretul sa apara ca in restul paginii
        const money = cents => {
          const raw = window.Currency?.formatMoney ? window.Currency.formatMoney(cents, window.settings?.moneyFormat) : (Number(cents) / 100).toFixed(2) + ' Lei';
          return String(raw).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        };
        section.querySelectorAll('.product--actual-price').forEach(node => { const n = money(variant.price).match(/[\d.,]+/); const inner = node.querySelector('.money'); if (inner && n) inner.textContent = ' ' + n[0]; else node.textContent = money(variant.price); });
        /* 27.10 (audit): pornind de la un produs fara reducere, nodul pretului taiat lipsea -> il cream langa pretul actual */
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          section.querySelectorAll('.product--actual-price').forEach(node => {
            const wrap = node.parentElement;
            if (wrap && !wrap.querySelector('.product--cut-price')) { const cut = document.createElement('span'); cut.className = 'product--cut-price line-through'; node.after(cut); }
          });
        }
        section.querySelectorAll('.product--cut-price').forEach(node => {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            node.textContent = money(variant.compare_at_price); node.hidden = false;
          } else node.hidden = true;
        });
        // galeria completa a produsului ales (toate pozele + miniaturi), din Section Rendering API; rezerva: doar prima poza
        const image = variant.featured_image?.src || product.featured_image || product.images?.[0];
        let galleryDone = false;
        const needGallery = !!section.querySelector('media-gallery') && !!section.closest('[id^="shopify-section-"]'); /* 27.10: fara galerie in sectiune nu avem ce sincroniza */
        try {
          const host = section.closest('[id^="shopify-section-"]');
          const sid = host ? host.id.replace('shopify-section-', '') : '';
          const oldGallery = section.querySelector('media-gallery');
          if (sid && oldGallery) {
            /* 27.10 (audit): galeria are limita de 8 s (pe conexiune lenta nu tinem cumpararea blocata la nesfarsit) */
            const acG = typeof AbortController === 'function' ? new AbortController() : null;
            const toG = acG ? setTimeout(() => acG.abort(), 8000) : 0;
            const html = await fetch(root + 'products/' + encodeURIComponent(item.handle) + '?section_id=' + encodeURIComponent(sid), { credentials: 'same-origin', signal: acG ? acG.signal : undefined }).then(r => r.ok ? r.text() : '').finally(() => clearTimeout(toG));
            if (stale()) return; /* 27.10: intre timp s-a ales alt produs */
            const doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;
            const fresh = doc && doc.querySelector('media-gallery');
            if (fresh) {
              fresh.querySelectorAll('img[data-src], img[data-srcset]').forEach(im => { if (im.dataset.src && !im.getAttribute('src')) im.src = im.dataset.src; });
              oldGallery.replaceWith(document.importNode(fresh, true));
              galleryDone = true;
              // descrierea, recenziile si detaliile produsului ales (altfel ramaneau de la nuanta veche)
              ['.product--description', '#recenzii', '.cc-facts-grid'].forEach(sel => {
                const olds = section.closest('[id^="shopify-section-"]').querySelectorAll(sel), news = doc.querySelectorAll(sel);
                olds.forEach((o, k) => { if (news[k]) o.replaceWith(document.importNode(news[k], true)); });
              });
              try { if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') window.jdgm.initializeWidgets(); } catch (_) {}
              // culoarea ojei din sticluta robotelului urmeaza nuanta aleasa (#rrggbb)
              try {
                const hx = (doc.querySelector('[data-cf-hex]')?.getAttribute('data-cf-hex') || '').trim();
                let pm = document.querySelector('[data-coria-paint-color]');
                if (/^#[0-9a-f]{6}$/i.test(hx)) { if (!pm) { pm = document.createElement('meta'); document.head.append(pm); } pm.setAttribute('data-coria-paint-color', hx); }
              } catch (_) {}
              const crumbs = document.querySelectorAll('.breadcrumb li:last-child, [class*=breadcrumb] li:last-child span, [class*=breadcrumb] [aria-current]');
              crumbs.forEach(c => { if (c.children.length === 0) c.textContent = product.title; });
              try { document.title = product.title + ' – Coria'; } catch (_) {}
              const nu = root + 'products/' + item.handle + location.search;
              if (location.pathname.indexOf('/products/' + item.handle) === -1) history.replaceState(history.state, '', nu);
              window.dispatchEvent(new Event('resize'));
              document.dispatchEvent(new CustomEvent('coria:gallery-replaced', { detail: { handle: item.handle } }));
            }
          }
        } catch (_) { galleryDone = false; }
        if (stale()) return; /* 27.10 (audit): si dupa o galerie esuata — intre timp s-a ales alt produs */
        /* 27.10 (audit NOU 6): daca galeria produsului ales nu s-a putut incarca (eroare / 8 s), nu mai inlocuim doar prima poza
           (ramaneau pozele produsului vechi, cu cumpararea activa) -> deschidem pagina produsului ales; cumpararea ramane oprita pana atunci */
        if (!galleryDone && needGallery) {
          buyDone = true;
          status.textContent = 'Deschid produsul…';
          window.location.href = root + 'products/' + encodeURIComponent(item.handle);
          return;
        }
        block.querySelectorAll('[data-handle]').forEach(control => {
          const isSelected = control.dataset.handle === item.handle;
          control.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          control.classList.toggle('is-current', isSelected);
        });
        block.querySelectorAll('.coria-family-card').forEach(card => {
          card.classList.toggle('is-current', Boolean(card.querySelector(`[data-handle="${CSS.escape(item.handle)}"]`)));
        });
        const volume = item.volume ? ' · ' + item.volume : '';
        selected.textContent = product.title + volume;
        status.textContent = product.title + volume + (canAdd ? ' – selectat. Îl poți adăuga în coș.' : ' – momentan indisponibil.');
        this.dataset.selectedHandle = item.handle;
        if (!canAdd) button.classList.add('is-unavailable');
        else button.classList.remove('is-unavailable');
        button.disabled = !canAdd;
        button.setAttribute('aria-disabled', canAdd ? 'false' : 'true');
        if (stale()) return;
        /* 27.10 (audit): abia acum, cu galeria si selectia afisata la zi, cumpararea se deblocheaza pentru produsul ales */
        section.querySelectorAll('[data-add-to-cart], [data-sticky-buy-button]').forEach(control => {
          control.disabled = !canAdd;
          if (canAdd) control.removeAttribute('aria-disabled');
          else control.setAttribute('aria-disabled', 'true');
        });
        buyDone = true;
        /* 27.10 (audit NOU 6 runda 3): selectorul nativ de variante apartine produsului initial; dupa o alegere Coria a altui produs
           o alegere nativa ar rescrie formularul cu varianta produsului initial -> il ascundem si il dezactivam (revine la produsul initial) */
        section.querySelectorAll('variants-set').forEach(v => { const own = !v.dataset.url || v.dataset.url.split('?')[0].replace(/\/$/, '').endsWith('/products/' + item.handle); v.hidden = !own; v.inert = !own; v.dataset.cfLocked = own ? '' : '1'; if (!own) v.setAttribute('aria-hidden', 'true'); else v.removeAttribute('aria-hidden'); });
        document.dispatchEvent(new CustomEvent('coria:selection-done', { detail: { handle: item.handle, available: canAdd } })); /* 27.10: bara mobila se sincronizeaza */
      } catch (error) {
        if (stale()) return; /* 27.10 (audit): eroarea unei alegeri vechi nu mai deschide alt produs peste cea noua */
        if (!aplicat) { buyDone = true; /* 27.10 (audit NOU 6): cumpararea ramane oprita pana la navigare (formularul si poza nu se potrivesc) */ status.textContent = 'Deschid produsul…'; window.location.href = root + 'products/' + encodeURIComponent(item.handle); return; }
        /* 27.10 (audit NOU 6): eroare dupa ce formularul a primit deja produsul nou -> tot pagina produsului ales (coerent), nu cumparare activa cu alta poza */
        console.warn('Coria product selection:', error.message);
        buyDone = true; status.textContent = 'Deschid produsul…'; window.location.href = root + 'products/' + encodeURIComponent(item.handle);
      } finally {
        if (!button.classList.contains('is-unavailable')) button.disabled = false;
        if (!stale()) {
          block.removeAttribute('aria-busy'); /* 27.10: alegerea noua inca lucreaza */
          if (!buyDone) buyControls.forEach(control => { control.disabled = false; control.removeAttribute('aria-disabled'); }); /* eroare: cumpararea revine cum era */
        }
      }
    }
  }

  customElements.define('coria-product-choices', CoriaProductChoices);

  /* 18.10.2026: tot cardul de nuanta e apasabil (poza, numele), nu doar pastila de marime */
  document.addEventListener('click', (event) => {
    const card = event.target.closest && event.target.closest('.coria-family-card');
    if (!card || event.target.closest('button, a, input, label')) return;
    const options = [...card.querySelectorAll('.coria-family-size:not([disabled])')];
    /* pastram linia pe care e clienta (ex. Marble Ink vs Marble Ink Metallic): acelasi inceput de handle ca produsul curent */
    const current = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '';
    const stem = current.replace(/[-_]?[a-z]*\d+[a-z]*$/i, '');
    const option = (stem && options.find(o => (o.dataset.handle || '').replace(/[-_]?[a-z]*\d+[a-z]*$/i, '') === stem)) || options[0];
    if (option && !option.classList.contains('is-current')) option.click();
  });
})();

/* 19.10.2026: tastatura in alegerea nuantei / marimii / aromei (de lipit la finalul assets/coria-product-choices.js).
   Inainte: fiecare pastila era o oprire de Tab (24 la Marble Ink), iar sagetile nu faceau nimic.
   Acum fiecare lista e un grup cu nume: un singur Tab intra pe optiunea aleasa, sagetile (si Home/End) muta focusul,
   Enter/Spatiu aleg (butoane native, acelasi drum ca un click). Listele se redeseneaza (cautare, „Arata mai multe”),
   deci ordinea Tab se reface la fiecare schimbare, cu un observator doar pe coria-product-choices. */
(() => {
  if (window.__cfChoicesKeys) return; window.__cfChoicesKeys = 1;
  const OPT = ':is(.coria-family-size, .coria-choice)[aria-pressed]';
  const LIST = '.coria-family-list, .coria-choice-list';
  const visible = (e) => !e.disabled && e.getClientRects().length > 0;
  const opts = (list) => [...list.querySelectorAll(OPT)].filter(visible);
  const label = (list) => {
    if (list.hasAttribute('aria-label') || list.hasAttribute('aria-labelledby')) return;
    /* titlul cel mai apropiat de deasupra listei (la blocurile cu doua liste: „Colectie” si „Nuanta” au titluri separate) */
    let p = list.previousElementSibling, h = null;
    while (p && !h) { h = p.matches('h3, .coria-choice-heading') ? (p.querySelector('h3') || p) : p.querySelector('h3'); p = p.previousElementSibling; }
    const block = list.closest('.coria-choice-group');
    if (h && h.id) list.setAttribute('aria-labelledby', h.id);
    else if (h && h.textContent.trim()) list.setAttribute('aria-label', h.textContent.trim());
    else if (block && block.getAttribute('aria-labelledby')) list.setAttribute('aria-labelledby', block.getAttribute('aria-labelledby'));
  };
  const rove = (list, keep) => {
    const o = opts(list); if (!o.length) return;
    list.setAttribute('role', 'group'); label(list);
    const on = (keep && o.includes(keep) && keep) || o.find(x => x.getAttribute('aria-pressed') === 'true') || o[0];
    o.forEach(x => { x.tabIndex = x === on ? 0 : -1; });
  };
  const all = (root) => (root || document).querySelectorAll(LIST).forEach(l => rove(l));
  document.addEventListener('keydown', (e) => {
    const t = e.target; if (!t.matches || !t.matches(OPT)) return;
    const list = t.closest(LIST); if (!list) return;
    const o = opts(list), i = o.indexOf(t); if (i < 0) return;
    let n = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = o[(i + 1) % o.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = o[(i - 1 + o.length) % o.length];
    else if (e.key === 'Home') n = o[0];
    else if (e.key === 'End') n = o[o.length - 1];
    if (!n) return;
    e.preventDefault();
    rove(list, n); n.focus();
  });
  /* dupa Tab-ul de iesire, la revenire intra din nou pe optiunea aleasa */
  document.addEventListener('focusout', (e) => {
    const t = e.target; if (!t.matches || !t.matches(OPT)) return;
    const list = t.closest(LIST); if (list && !list.contains(e.relatedTarget)) setTimeout(() => rove(list), 0);
  });
  /* dupa alegere pagina isi schimba produsul (galerie, descriere, uneori chiar lista): butonul apasat poate disparea
     si focusul cadea pe BODY. Il punem pe optiunea cu acelasi produs din lista noua (sau pe cea aleasa). */
  let lastOpt = null, lastHandle = '', lastAct = 0;
  /* doar imediat dupa o alegere (4 s), ca sa nu furam focusul cand clienta a facut click in alta parte */
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest(OPT)) lastAct = Date.now(); }, true);
  document.addEventListener('focusin', (e) => { if (e.target.matches && e.target.matches(OPT)) { lastOpt = e.target; lastHandle = e.target.dataset.handle || ''; } else lastOpt = null; });
  const restore = () => {
    if (!lastOpt || Date.now() - lastAct > 4000 || (document.activeElement && document.activeElement !== document.body)) return;
    const c = [...document.querySelectorAll(OPT)].filter(visible);
    const n = (lastHandle && c.find(x => x.dataset.handle === lastHandle)) || c.find(x => x.getAttribute('aria-pressed') === 'true');
    if (n) { const l = n.closest(LIST); if (l) rove(l, n); n.focus({ preventScroll: true }); }
  };
  let q = 0;
  const later = () => { if (!q) q = requestAnimationFrame(() => { q = 0; all(); restore(); }); };
  const watch = () => {
    document.querySelectorAll('coria-product-choices').forEach(el => {
      if (el.__cfKeys) return; el.__cfKeys = 1;
      new MutationObserver(later).observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'hidden', 'disabled'] });
    });
    all();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  /* elementele pot aparea dupa incarcare (sectiuni AJAX): reverificam la evenimentul galeriei si o data la load */
  document.addEventListener('coria:gallery-replaced', () => { watch(); setTimeout(restore, 60); });
  window.addEventListener('load', watch, { once: true });
})();
